<script setup lang="ts">
import Divider from '@components/Divider.vue';
import MenuList from '@layout/nav-v2/desktop/components/MenuList.vue';
import IconWhatsapp from '@icons/ph_whatsapp_gray-darker.svg';
import { trans } from '@elements/translation.ts';
import Icon from '@components/Icon.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { storeToRefs } from 'pinia';
import SubmenuItem from '@layout/nav-v2/SubmenuItem.vue';
import IconQuestionCircle from '@icons/ph_question-circle.svg';
import { URL_CONTACT } from '@tools/routes/routes';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconChatText from '@icons/ph_chat-text.svg';
import Typography from '@components/Typography.vue';
import { showNewMessage } from '@intercom/messenger-js-sdk';
import IconPhoneInbound from '@icons/ph_phone-inbound.svg';

const emit = defineEmits<{(e: 'close'): void }>();

const navbarStore = useNavbarStore();
const {
  whatsappLink,
  faqLink,
  calendlyUrl,
  bookAppointmentUrl,
} = storeToRefs(navbarStore);

const openIntercom = (): void => {
  showNewMessage('');
  emit('close');
};

const openLoginModal = (): void => {
  navbarStore.openLoginModal();
  emit('close');
};

const openRegisterModal = (): void => {
  navbarStore.openRegisterModal();
  emit('close');
};
</script>

<template>
  <MenuList class="flex flex-col gap-y-2">
    <li>
      <MenuList
        :class="[
          'px-2',
          'flex flex-col gap-y-2',
        ]"
      >
        <li>
          <SubmenuItem
            class="h-12"
            data-cy-ref="anonymous-menu__item--login"
            @click="openLoginModal"
          >
            <Typography variant="body">
              {{ trans('user_menu.login') }}
            </Typography>
          </SubmenuItem>
        </li>

        <li>
          <SubmenuItem
            class="h-12"
            data-cy-ref="anonymous-menu__item--register"
            @click="openRegisterModal"
          >
            <Typography variant="body">
              {{ trans('user_menu.register') }}
            </Typography>
          </SubmenuItem>
        </li>
      </MenuList>
    </li>

    <li>
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <MenuList
        :class="[
          'px-2',
          'flex flex-col gap-y-2',
        ]"
      >
        <li v-if="whatsappLink">
          <SubmenuItem
            :link="whatsappLink"
            class="h-12"
            data-cy-ref="anonymous-menu__item--whatsapp"
          >
            <div class="desktop-anonymous-menu__item-content">
              <Icon
                :svg="IconWhatsapp"
                width="24"
                height="24"
                fill="gray-darker"
              />

              <Typography variant="body">
                {{ trans('user_menu.whatsapp') }}
              </Typography>
            </div>
          </SubmenuItem>
        </li>

        <li>
          <SubmenuItem
            :class="[
              'h-12',
              'hover:!bg-transparent',
              'cursor-default'
            ]"
          >
            <div class="desktop-anonymous-menu__item-content">
              <Icon
                :svg="IconPhoneInbound"
                width="24"
                height="24"
                stroke="gray-darker"
                has-icon-stroke
              />

              <Typography variant="body">
                {{ trans('user_menu.recalled') }}
              </Typography>
            </div>
          </SubmenuItem>

          <MenuList
            :class="[
              'ml-6',
              'border-0 border-l border-solid border-gray',
              'px-2'
            ]"
          >
            <SubmenuItem
              class="px-3 py-2"
              :link="calendlyUrl ?? undefined"
              data-cy-ref="anonymous-menu__item--find-office"
            >
              <Typography variant="body">
                {{ trans('user_menu.find_office') }}
              </Typography>
            </SubmenuItem>

            <SubmenuItem
              class="px-3 py-2"
              :link="bookAppointmentUrl ?? undefined"
              data-cy-ref="anonymous-menu__item--publish-office"
            >
              <Typography variant="body">
                {{ trans('user_menu.publish_office') }}
              </Typography>
            </SubmenuItem>
          </MenuList>
        </li>

        <li>
          <SubmenuItem
            class="h-12"
            data-cy-ref="anonymous-menu__item--intercom"
            @click="openIntercom"
          >
            <div class="desktop-anonymous-menu__item-content">
              <Icon
                :svg="IconChatText"
                width="24"
                height="24"
                fill="gray-darker"
              />

              <Typography variant="body">
                {{ trans('user_menu.chat') }}
              </Typography>
            </div>
          </SubmenuItem>
        </li>

        <li>
          <SubmenuItem
            :link="URL_CONTACT"
            class="h-12"
            data-cy-ref="anonymous-menu__item--message"
          >
            <div class="desktop-anonymous-menu__item-content">
              <Icon
                :svg="IconEnvelope"
                width="24"
                height="24"
                fill="gray-darker"
              />

              <Typography variant="body">
                {{ trans('user_menu.send_message') }}
              </Typography>
            </div>
          </SubmenuItem>
        </li>
      </MenuList>
    </li>

    <li>
      <Divider class="m-0" color="gray" />
    </li>

    <li
      v-if="faqLink"
      class="px-2"
    >
      <SubmenuItem
        :link="faqLink"
        class="h-12"
        data-cy-ref="anonymous-menu__item--faq"
      >
        <div class="desktop-anonymous-menu__item-content">
          <Icon
            :svg="IconQuestionCircle"
            width="24"
            height="24"
            fill="gray-darker"
          />

          <Typography variant="body">
            {{ trans('user_menu.faq') }}
          </Typography>
        </div>
      </SubmenuItem>
    </li>
  </MenuList>
</template>

<style scoped lang="scss">
.desktop-anonymous-menu {
  &__item-content {
    @apply flex items-center gap-x-2;
  }
}
</style>
